import { useState, useEffect } from 'react';
import CampaignCard from '../components/CampaignCard';
import DBadge from '../components/DBadge';
import ChannelBadge from '../components/ChannelBadge';
import Toast from '../components/Toast';
import FormInput from '../components/auth/FormInput';
import {
  IconUsers, IconBookmark, IconBack, IconShare, IconBlog, IconInsta,
  IconYoutube, IconNaver, IconBell, IconHeart, IconTable, IconX,
} from '../components/Icons';
import gnb from '../components/GNB.module.css';
import hero from '../components/HeroBanner.module.css';
import login from './LoginPage.module.css';
import loginForm from '../components/auth/LoginForm.module.css';
import s from './DesignSystemPage.module.css';
import Logo from '../components/Logo';

/* ────────────────────────────────────────────────
   토큰 데이터 — styles/global.css :root 와 1:1 대응
   ──────────────────────────────────────────────── */

const NEUTRALS = [
  ['--white', '#FFFFFF'],
  ['--black', '#000000'],
];

const GRAYS = [
  ['--gray-50', '#FAFAFA'],
  ['--gray-100', '#F5F5F5'],
  ['--gray-200', '#EEEEEE'],
  ['--gray-300', '#E0E0E0'],
  ['--gray-400', '#BDBDBD'],
  ['--gray-500', '#9E9E9E'],
  ['--gray-600', '#757575'],
  ['--gray-700', '#616161'],
  ['--gray-800', '#424242'],
  ['--gray-900', '#212121'],
];

const BRAND = [
  ['--primary', '#E84040'],
  ['--primary-light', '#FF8C5A'],
  ['--primary-dark', '#E5501A'],
  ['--success', '#34C759'],
  ['--error', '#FF3B30'],
];

const TYPE_SCALE = [
  ['--text-3xl', 26],
  ['--text-2xl', 22],
  ['--text-xl', 20],
  ['--text-lg', 18],
  ['--text-md', 17],
  ['--text-base', 16],
  ['--text-sm', 14],
  ['--text-xs', 13],
];

const WEIGHTS = [
  [400, 'Regular'],
  [500, 'Medium'],
  [600, 'SemiBold'],
  [700, 'Bold'],
];

const SPACING = [
  ['--space-xs', 4],
  ['--space-sm', 8],
  ['--space-md', 12],
  ['--space-base', 16],
  ['--space-lg', 20],
  ['--space-xl', 24],
  ['--space-2xl', 32],
  ['--space-3xl', 40],
  ['--space-4xl', 48],
];

const RADII = [
  ['--radius-sm', 4],
  ['--radius-md', 8],
  ['--radius-lg', 12],
  ['--radius-xl', 16],
  ['--radius-full', 9999],
];

const SHADOWS = [
  ['--shadow-sm', '0 1px 4px rgba(0,0,0,0.06)'],
  ['--shadow-card', '0 3px 10px rgba(0,0,0,0.08)'],
  ['--shadow-md', '0 4px 16px rgba(0,0,0,0.10)'],
  ['--shadow-lg', '0 8px 32px rgba(0,0,0,0.12)'],
];

const ICON_SET = [
  ['IconUsers', IconUsers, 20],
  ['IconBookmark', IconBookmark, 20],
  ['IconBack', IconBack, 20],
  ['IconShare', IconShare, 20],
  ['IconBell', IconBell, 20],
  ['IconHeart', IconHeart, 20],
  ['IconTable', IconTable, 20],
  ['IconX', IconX, 20],
  ['IconBlog', IconBlog, 20],
  ['IconInsta', IconInsta, 20],
  ['IconYoutube', IconYoutube, 20],
  ['IconNaver', IconNaver, 20],
];

const CARD_STATES = [
  {
    label: '기본 (D-12)',
    isScrapped: false,
    campaign: {
      id: 'ds-1', title: '강남 신상 파스타 맛집 신메뉴 체험단 모집',
      brandName: '파스타랩', recruitCount: 10, dday: 12,
    },
  },
  {
    label: '마감임박 (D-2)',
    isScrapped: false,
    campaign: {
      id: 'ds-2', title: '수제버거 신메뉴 시식단 모집',
      brandName: '버거하우스', recruitCount: 5, dday: 2,
    },
  },
  {
    label: 'D-DAY',
    isScrapped: false,
    campaign: {
      id: 'ds-3', title: '오마카세 디너 코스 체험단',
      brandName: '스시젠', recruitCount: 3, dday: 0,
    },
  },
  {
    label: '마감 + 저장됨',
    isScrapped: true,
    campaign: {
      id: 'ds-4', title: '브런치 카페 신메뉴 체험단 모집합니다',
      brandName: '모닝테이블', recruitCount: 8, dday: -1,
    },
  },
];

/* ────────────────────────────────────────────────
   레이아웃 헬퍼
   ──────────────────────────────────────────────── */

function Section({ id, title, note, children }) {
  return (
    <section className={s.section} id={id}>
      <div className={s.sectionHead}>
        <h2 className={s.sectionTitle}>{title}</h2>
        {note && <p className={s.sectionNote}>{note}</p>}
      </div>
      {children}
    </section>
  );
}

function Specimen({ label, spec, children, align = 'start' }) {
  return (
    <div className={s.specimen}>
      <div className={s.specimenStage} style={{ alignItems: align }}>{children}</div>
      <p className={s.specimenLabel}>{label}</p>
      {spec && <p className={s.specimenSpec}>{spec}</p>}
    </div>
  );
}

function Swatch({ token, hex, dark }) {
  return (
    <div className={s.swatch}>
      <div
        className={`${s.swatchChip}${dark ? ` ${s.swatchChipBordered}` : ''}`}
        style={{ background: hex }}
      />
      <p className={s.swatchToken}>{token}</p>
      <p className={s.swatchHex}>{hex}</p>
    </div>
  );
}

/* ────────────────────────────────────────────────
   페이지
   ──────────────────────────────────────────────── */

export default function DesignSystemPage() {
  const [vw, setVw] = useState(typeof window === 'undefined' ? 0 : window.innerWidth);

  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const noop = () => {};

  return (
    <div className={s.page}>
      <header className={s.pageHead}>
        <p className={s.eyebrow}>Design System</p>
        <h1 className={s.pageTitle}>
          다<span>잇</span> · 디자인 시스템
        </h1>
        <p className={s.pageDesc}>
          styles/global.css 의 디자인 토큰과 실제 컴포넌트를 상태별로 나열한 캡처용 페이지입니다.
          모든 요소는 실제 컴포넌트와 CSS Module을 그대로 렌더링하므로 코드와 100% 일치합니다.
        </p>
        <div className={s.vwPill}>
          현재 뷰포트 <strong>{vw}px</strong>
          <span className={s.vwHint}>
            {vw <= 600 ? '모바일 레이아웃' : vw < 768 ? '태블릿 구간' : '데스크톱 레이아웃'}
          </span>
        </div>
      </header>

      {/* ── 1. 색상 ───────────────────────────── */}
      <Section id="color" title="01 · 색상" note="Colors">
        <h3 className={s.subTitle}>Neutral</h3>
        <div className={s.swatchGrid}>
          {NEUTRALS.map(([t, h]) => <Swatch key={t} token={t} hex={h} dark />)}
        </div>

        <h3 className={s.subTitle}>Gray Scale</h3>
        <div className={s.swatchGrid}>
          {GRAYS.map(([t, h]) => <Swatch key={t} token={t} hex={h} dark />)}
        </div>

        <h3 className={s.subTitle}>Brand &amp; Semantic</h3>
        <div className={s.swatchGrid}>
          {BRAND.map(([t, h]) => <Swatch key={t} token={t} hex={h} />)}
        </div>
      </Section>

      {/* ── 2. 타이포그래피 ────────────────────── */}
      <Section id="type" title="02 · 타이포그래피" note="Pretendard Variable · letter-spacing em 기반">
        <div className={s.typeList}>
          {TYPE_SCALE.map(([token, px]) => (
            <div className={s.typeRow} key={token}>
              <div className={s.typeMeta}>
                <p className={s.typeToken}>{token}</p>
                <p className={s.typePx}>{px}px</p>
              </div>
              <p className={s.typeSample} style={{ fontSize: px }}>
                체험단을 찾는 가장 쉬운 방법
              </p>
            </div>
          ))}
        </div>

        <h3 className={s.subTitle}>Weights</h3>
        <div className={s.typeList}>
          {WEIGHTS.map(([w, name]) => (
            <div className={s.typeRow} key={w}>
              <div className={s.typeMeta}>
                <p className={s.typeToken}>{name}</p>
                <p className={s.typePx}>{w}</p>
              </div>
              <p className={s.typeSample} style={{ fontSize: 18, fontWeight: w }}>
                다잇에서 새로운 캠페인을 만나보세요
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* ── 3. 스페이싱 ───────────────────────── */}
      <Section id="space" title="03 · 스페이싱" note="Spacing scale">
        <div className={s.spaceList}>
          {SPACING.map(([token, px]) => (
            <div className={s.spaceRow} key={token}>
              <div className={s.typeMeta}>
                <p className={s.typeToken}>{token}</p>
                <p className={s.typePx}>{px}px</p>
              </div>
              <div className={s.spaceBar} style={{ width: px }} />
            </div>
          ))}
        </div>
      </Section>

      {/* ── 4. 라운드 & 섀도우 ─────────────────── */}
      <Section id="radius" title="04 · 라운드 &amp; 섀도우" note="Border radius / Elevation">
        <h3 className={s.subTitle}>Border Radius</h3>
        <div className={s.tileRow}>
          {RADII.map(([token, px]) => (
            <Specimen key={token} label={token} spec={px === 9999 ? '9999px' : `${px}px`}>
              <div className={s.radiusTile} style={{ borderRadius: px }} />
            </Specimen>
          ))}
        </div>

        <h3 className={s.subTitle}>Shadows</h3>
        <div className={s.tileRow}>
          {SHADOWS.map(([token, value]) => (
            <Specimen key={token} label={token} spec={value}>
              <div className={s.shadowTile} style={{ boxShadow: value }} />
            </Specimen>
          ))}
        </div>
      </Section>

      {/* ── 5. 아이콘 ─────────────────────────── */}
      <Section id="icon" title="05 · 아이콘" note="components/Icons.jsx · stroke 기반, currentColor 상속">
        <div className={s.iconGrid}>
          {ICON_SET.map(([name, Icon, size]) => (
            <div className={s.iconCell} key={name}>
              <div className={s.iconBox}><Icon size={size} /></div>
              <p className={s.iconName}>{name}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ── 6. 버튼 ───────────────────────────── */}
      <Section id="button" title="06 · 버튼" note="GNB.module.css · LoginPage.module.css">
        <div className={s.tileRow}>
          <Specimen label="Solid / 34px" spec="gray-900 bg · text-xs 700">
            <button className={gnb.solidBtn}>로그인</button>
          </Specimen>
          <Specimen label="Outline / 34px" spec="1.5px gray-900 border">
            <button className={gnb.outlineBtn}>로그아웃</button>
          </Specimen>
          <Specimen label="Text / 34px" spec="gray-900 · text-xs 600">
            <button className={gnb.textBtn}>회원가입</button>
          </Specimen>
          <Specimen label="Icon / 40×40" spec="radius-md · 호버 시 gray-100 배경">
            <button className={gnb.navBtn}><IconBookmark size={20} /></button>
          </Specimen>
        </div>

        <h3 className={s.subTitle}>Submit — 52px full width</h3>
        <div className={s.tileRow}>
          <Specimen label="Disabled (기본)" spec="gray-200 bg · gray-700 text">
            <div className={s.btnStage}>
              <button className={loginForm.submitBtn}>로그인</button>
            </div>
          </Specimen>
          <Specimen label="Active" spec="gray-900 bg · white text">
            <div className={s.btnStage}>
              <button className={`${loginForm.submitBtn} ${loginForm.active}`}>로그인</button>
            </div>
          </Specimen>
        </div>

        <h3 className={s.subTitle}>Links</h3>
        <div className={s.tileRow}>
          <Specimen label="Inline link" spec="text-sm · gray-600">
            <span className={login.link}>계정찾기</span>
          </Specimen>
          <Specimen label="Underline link" spec="text-sm · gray-900 · underline">
            <span className={login.signupLink}>회원가입</span>
          </Specimen>
        </div>
      </Section>

      {/* ── 7. 폼 인풋 ────────────────────────── */}
      <Section id="input" title="07 · 폼 인풋" note="components/auth/FormInput.jsx · 52px height, radius-lg">
        <div className={s.formGrid}>
          <div className={s.formCell}>
            <FormInput label="이메일" placeholder="이메일을 입력해주세요" value="" onChange={noop} />
            <p className={s.cellLabel}>Empty (placeholder)</p>
          </div>
          <div className={s.formCell}>
            <FormInput label="이메일" value="koony000415@gmail.com" onChange={noop} />
            <p className={s.cellLabel}>Filled</p>
          </div>
          <div className={s.formCell}>
            <FormInput
              label="이메일" value="koony000415"
              onChange={noop} error="올바른 이메일 형식이 아닙니다"
            />
            <p className={s.cellLabel}>Error</p>
          </div>
          <div className={s.formCell}>
            <FormInput
              label="이메일" value="koony000415@gmail.com"
              onChange={noop} success="사용 가능한 이메일입니다"
            />
            <p className={s.cellLabel}>Success</p>
          </div>
          <div className={s.formCell}>
            <FormInput
              label="비밀번호" type="password" placeholder="비밀번호를 입력해주세요"
              value="password1234" onChange={noop}
            />
            <p className={s.cellLabel}>Password (토글 버튼)</p>
          </div>
          <div className={s.formCell}>
            <FormInput label="아이디" value="변경할 수 없습니다" onChange={noop} disabled />
            <p className={s.cellLabel}>Disabled</p>
          </div>
        </div>
        <p className={s.footnote}>
          Focus 상태는 <code>:focus</code> 의사클래스라 인풋을 직접 클릭해야 나타납니다 —
          배경 white + 1.5px gray-900 보더. 캡처할 때 한 칸 클릭 후 별도 상태로 찍어주세요.
        </p>
      </Section>

      {/* ── 8. 배지 ───────────────────────────── */}
      <Section id="badge" title="08 · 배지" note="DBadge · ChannelBadge · HeroBanner badge">
        <h3 className={s.subTitle}>D-Day 배지 — 썸네일 좌상단 오버레이</h3>
        <div className={s.tileRow}>
          {[
            [12, '기본 (D-4 이상)', 'rgba(0,0,0,0.55)'],
            [2, '마감임박 (D-1~3)', '#F97316'],
            [0, 'D-DAY', '--primary'],
            [-1, '마감', 'rgba(0,0,0,0.45)'],
          ].map(([dday, label, bg]) => (
            <Specimen key={label} label={label} spec={String(bg)}>
              <div className={s.badgeStage}><DBadge dday={dday} /></div>
            </Specimen>
          ))}
        </div>

        <h3 className={s.subTitle}>채널 배지 — pill, 11px 600</h3>
        <div className={s.tileRow}>
          {[
            ['blog', '블로그'], ['insta', '인스타'],
            ['youtube', '유튜브'], ['naver', '네이버'],
          ].map(([type, label]) => (
            <Specimen key={type} label={label} spec={`type="${type}"`}>
              <ChannelBadge type={type} />
            </Specimen>
          ))}
        </div>

        <h3 className={s.subTitle}>히어로 상태 배지 — 이미지 위 글래스</h3>
        <div className={s.heroBadgeStage}>
          <span className={hero.badge}><span className={hero.dot} />모집중</span>
          <span className={`${hero.badge} ${hero.badgeClosed}`}>
            <span className={hero.dot} />모집마감
          </span>
        </div>
      </Section>

      {/* ── 9. 카드 ───────────────────────────── */}
      <Section id="card" title="09 · 캠페인 카드" note="components/CampaignCard.jsx · radius-xl, shadow-card, 1:1 썸네일">
        <div className={s.cardGrid}>
          {CARD_STATES.map(({ label, campaign, isScrapped }) => (
            <div className={s.cardCell} key={campaign.id}>
              <CampaignCard campaign={campaign} onBookmark={noop} isScrapped={isScrapped} />
              <p className={s.cellLabel}>{label}</p>
            </div>
          ))}
        </div>
        <p className={s.footnote}>
          썸네일 이미지가 없을 때는 gray-100 배경에 음식 이모지 폴백이 렌더링됩니다 (실제 컴포넌트 동작).
          hover 시 <code>translateY(-3px)</code> + 썸네일 <code>scale(1.04)</code> — 별도 상태로 캡처하세요.
        </p>
      </Section>

      {/* ── 10. 내비게이션 ─────────────────────── */}
      <Section id="nav" title="10 · GNB" note="components/GNB.module.css · height 56px, max-width 1200px">
        <div className={s.navStage}>
          <nav className={gnb.gnb} style={{ position: 'static' }}>
            <div className={gnb.inner}>
              <button className={gnb.logo}><Logo height={22} /></button>
              <div className={gnb.right}>
                <button className={gnb.textBtn}>회원가입</button>
                <button className={gnb.solidBtn}>로그인</button>
              </div>
            </div>
          </nav>
        </div>
        <p className={s.cellLabel}>로그아웃 상태</p>

        <div className={s.navStage} style={{ marginTop: 24 }}>
          <nav className={gnb.gnb} style={{ position: 'static' }}>
            <div className={gnb.inner}>
              <button className={gnb.logo}><Logo height={22} /></button>
              <div className={gnb.right}>
                <button className={gnb.navBtn}><IconBookmark size={20} /></button>
                <button className={gnb.outlineBtn}>로그아웃</button>
              </div>
            </div>
          </nav>
        </div>
        <p className={s.cellLabel}>로그인 상태</p>
      </Section>

      {/* ── 11. 토스트 ────────────────────────── */}
      <Section id="toast" title="11 · 토스트" note="components/Toast.jsx · gray-900 pill, shadow-md">
        <div className={s.toastStage}>
          <Toast message="저장했어요" onDismiss={noop} duration={86400000} />
        </div>
        <p className={s.footnote}>
          실제 앱에서는 <code>position: fixed</code> 로 화면 하단 100px에 뜹니다.
          여기서는 캡처를 위해 <code>transform</code> 컨테이너 안에 가둬 인라인으로 표시했습니다.
        </p>
      </Section>

      <footer className={s.pageFoot}>
        <p>dait-react · design system capture sheet</p>
      </footer>
    </div>
  );
}
